// File: src/components/EventManagement.js
// Organizer-facing CRUD over race events (kilpailut).
//
// Organizers see only their own events; admins and editors see all. Ownership
// is enforced on the server - this component only decides what to render.

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, MapPin, Calendar, X, Users, Trophy, Copy } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { STAFF_ROLES, normalizeRole } from '../utils/roles';
import {
  CHAMPIONSHIPS, isChampionship, championshipBadgeClass, useChampionshipLabel
} from '../utils/championships';
import { TRANSPONDER_TYPES, parseTransponderSet } from '../utils/transponders';
import { usePermissions } from '../hooks/usePermissions';
import { formatDateForDisplay, getTodayISO, utcToLocalInput, localInputToUtc } from '../utils/utils';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import EventClassEditor from './EventClassEditor';
import EventQuestionEditor from './EventQuestionEditor';
import EntrantListView from './EntrantListView';
import CountryOptions from './CountryOptions';
import { getCountry } from '../data/countries';
import { useCountryName } from '../utils/countryNames';

const EMPTY_EVENT = {
  name: '',
  organizer: '',
  venue: '',
  address: '',
  city: '',
  country: 'FI',
  start_date: '',
  end_date: '',
  description: '',
  contact_email: '',
  contact_phone: '',
  website: '',
  entry_opens_at: '',
  entry_closes_at: '',
  entry_instructions: '',
  entry_list_public: true,
  external_entry: false,
  external_entry_url: '',
  supported_transponders: [],
  championship: 'none',
  status: 'draft'
};

// Literal class map - Tailwind cannot see runtime-built class names.
const STATUS_CLASSES = {
  draft: 'bg-paper-alt text-ink-muted',
  published: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
};

function EventManagement({ token, userRole }) {
  const countryName = useCountryName();
  const { t } = useLanguage();
  const championshipLabel = useChampionshipLabel();
  const permissions = usePermissions(userRole);
  // Putting an event on every country's calendar is an administrator's call.
  // The server enforces this; offering the control to anyone else would just
  // produce a 403 on save.
  const canMarkChampionship = permissions.isAdmin();
  const [events, setEvents] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [entrantsFor, setEntrantsFor] = useState(null);
  // The classes and extras a copy is carrying, held until the copy is saved.
  // Null for an ordinary create or edit. They cannot be edited before the
  // event exists - the two editors below need an id to post to - so the modal
  // says what is coming instead, and they become editable on first save.
  const [copying, setCopying] = useState(null);

  const isStaff = STAFF_ROLES.includes(normalizeRole(userRole));

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const query = isStaff && mineOnly ? '?mine=1' : '';
      setEvents(await apiGet(`/events${query}`, { token }));
      setError('');
    } catch (err) {
      setError(err.message || t('failedToLoadEvents'));
    } finally {
      setLoading(false);
    }
  }, [token, mineOnly, isStaff, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Series names for the class picker. Organizers cannot call the full
  // /api/series, so this uses the narrow options endpoint they do have.
  useEffect(() => {
    let cancelled = false;

    apiGet('/series/options', { token })
      .then(data => { if (!cancelled) setSeries(data); })
      .catch(err => {
        // Not fatal: classes can still be created without a series link.
        console.error('Could not load series for the class picker:', err.message);
      });

    return () => { cancelled = true; };
  }, [token]);

  // The event currently open in the modal, refreshed from the list so the class
  // editor sees its own additions.
  const editingEvent = events.find(e => e.id === editingId);

  const openCreate = () => {
    setEditingId(null);
    setCopying(null);
    setForm({ ...EMPTY_EVENT, start_date: getTodayISO() });
    setShowModal(true);
  };

  // A repeat event is the same event with different dates: same track, same
  // organizer, same classes, same extras. So everything carries except the
  // four date fields, which are exactly what differs and are left blank for
  // the organizer to fill in.
  //
  // This goes through the ordinary create form rather than a server-side
  // duplicate: nothing is written until the organizer has looked at it, and
  // POST /events already takes the classes and questions in one transaction.
  const openCopy = (event) => {
    fillFormFrom(event);
    setEditingId(null);
    setForm(prev => ({
      ...prev,
      name: t('eventCopySuffix').replace('{name}', event.name),
      start_date: '',
      end_date: '',
      entry_opens_at: '',
      entry_closes_at: '',
      // A copy with no dates yet has no business on the public calendar.
      status: 'draft'
    }));
    setCopying({
      // By series_class_id, never race_id: the copies link to the same
      // championship classes and get their own fresh rounds. Adopting the
      // original's results round would put two events' results in one place.
      classes: (event.classes || []).map(cls => ({
        name: cls.name,
        series_class_id: cls.series_class_id,
        entry_limit: cls.entry_limit
      })),
      // A conditional question names its trigger by POSITION in this list, not
      // by id: the copies get new ids, and the source event's mean nothing to
      // the new one. The server fills the real ids in as it inserts them.
      questions: (event.questions || []).map(q => ({
        label: q.label,
        help_text: q.help_text,
        answer_type: q.answer_type,
        required: q.required,
        min_value: q.min_value,
        max_value: q.max_value,
        depends_on_index: q.depends_on_question_id
          ? (event.questions || []).findIndex(other => other.id === q.depends_on_question_id)
          : undefined
      }))
    });
    setShowModal(true);
  };

  // How an event row becomes form state. Shared by edit and copy so the two
  // cannot disagree about which fields are booleans, which are sets, and which
  // are stored in UTC.
  const fillFormFrom = (event) => {
    setForm({
      ...EMPTY_EVENT,
      ...event,
      // Normalise nulls to '' so the inputs stay controlled.
      ...Object.fromEntries(Object.keys(EMPTY_EVENT)
        .filter(k => !['supported_transponders', 'entry_list_public', 'external_entry']
          .includes(k))
        .map(k => [k, event[k] ?? ''])),
      entry_list_public: Boolean(event.entry_list_public),
      external_entry: Boolean(event.external_entry),
      supported_transponders: parseTransponderSet(event.supported_transponders),
      // Stored UTC, shown in the organizer's own timezone.
      entry_opens_at: utcToLocalInput(event.entry_opens_at),
      entry_closes_at: utcToLocalInput(event.entry_closes_at)
    });
  };

  const openEdit = (event) => {
    fillFormFrom(event);
    setEditingId(event.id);
    setCopying(null);
    setShowModal(true);
  };

  const toggleTransponder = (value) => {
    setForm(prev => ({
      ...prev,
      supported_transponders: prev.supported_transponders.includes(value)
        ? prev.supported_transponders.filter(v => v !== value)
        : [...prev.supported_transponders, value]
    }));
  };

  const saveEvent = async () => {
    if (!form.name.trim()) {
      setError(t('eventNameRequired'));
      return;
    }
    if (!form.start_date) {
      setError(t('eventStartDateRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Dates stay as the plain YYYY-MM-DD the date input produces; routing them
      // through a Date would shift them a day for UTC+n users.
      const payload = {
        ...form,
        supported_transponders: form.supported_transponders.join(','),
        // The organizer types local time; the server stores and compares UTC.
        entry_opens_at: localInputToUtc(form.entry_opens_at),
        entry_closes_at: localInputToUtc(form.entry_closes_at)
      };

      if (editingId) {
        await apiPut(`/events/${editingId}`, payload, { token });
      } else {
        // A copy sends its classes and extras with the create, so the whole
        // event lands in one transaction rather than as a create followed by a
        // string of child writes that can half-fail.
        const created = await apiPost('/events',
          copying ? { ...payload, ...copying } : payload, { token });
        setEditingId(created.id);
        // Written now, so the class and extras editors below take over from
        // here and the summary line has nothing left to promise.
        setCopying(null);
      }

      await loadEvents();
      if (editingId) setShowModal(false);
    } catch (err) {
      setError(err.message || t('failedToSaveEvent'));
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(t('deleteEventConfirm'))) return;

    try {
      await apiDelete(`/events/${event.id}`, { token });
      await loadEvents();
    } catch (err) {
      // The server refuses to delete an event that has entries and says so.
      alert(err.message || t('failedToDeleteEvent'));
    }
  };

  const statusLabel = (status) => ({
    draft: t('eventStatusDraft'),
    published: t('eventStatusPublished'),
    cancelled: t('eventStatusCancelled')
  }[status] || status);

  const field = (key, label, type = 'text') => (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{label}</label>
      <input
        type={type}
        value={form[key] || ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
      />
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold text-ink">{t('eventManagement')}</h2>
        <div className="flex items-center gap-3">
          {isStaff && (
            <label className="flex items-center gap-2 text-ink-muted cursor-pointer">
              <input
                type="checkbox"
                checked={mineOnly}
                onChange={(e) => setMineOnly(e.target.checked)}
                className="w-4 h-4"
              />
              {t('eventMineOnly')}
            </label>
          )}
          <button
            onClick={openCreate}
            className="bg-accent hover:opacity-90 text-accent-ink px-6 py-2 rounded font-semibold flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('addEvent')}
          </button>
        </div>
      </div>

      {error && !showModal && (
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-ink-muted">{t('loading')}</p>
      ) : events.length === 0 ? (
        <div className="bg-paper-alt rounded-lg p-12 text-center">
          <Calendar className="w-12 h-12 text-ink-muted mx-auto mb-4" />
          <p className="text-ink-muted">{t('noEvents')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => (
            <div key={event.id} className="bg-paper-alt rounded-lg p-4 flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-ink font-semibold text-lg">{event.name}</span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${STATUS_CLASSES[event.status] || STATUS_CLASSES.draft}`}>
                    {statusLabel(event.status)}
                  </span>
                  {isChampionship(event.championship) && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded flex items-center gap-1 ${championshipBadgeClass(event.championship)}`}>
                      <Trophy className="w-3 h-3" />
                      {championshipLabel(event.championship)}
                    </span>
                  )}
                  {/* A championship someone else's country runs: on this list
                      because it is on this calendar, but not theirs to edit. */}
                  {event.can_edit === false && (
                    <span className="text-xs text-ink-muted">
                      {t('championshipReadOnly').replace(
                        '{country}',
                        countryName(getCountry(event.country)))}
                    </span>
                  )}
                </div>

                <div className="text-ink-muted text-sm mt-1 flex items-center gap-2 flex-wrap">
                  <Calendar className="w-4 h-4" />
                  {formatDateForDisplay(event.start_date)}
                  {event.end_date && ` – ${formatDateForDisplay(event.end_date)}`}
                  {(event.venue || event.city) && (
                    <>
                      <MapPin className="w-4 h-4 ml-2" />
                      {[event.venue, event.city].filter(Boolean).join(', ')}
                    </>
                  )}
                </div>

                {event.organizer && (
                  <div className="text-ink-muted text-sm mt-1">{event.organizer}</div>
                )}

                {event.classes && event.classes.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {event.classes.map(cls => (
                      <span key={cls.id} className="text-xs px-2 py-1 rounded bg-paper text-ink-muted border border-line">
                        {cls.name}
                        {cls.series_name ? ` → ${cls.series_name}` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* A championship another country runs is on this list to be read,
                  not acted on - including its entrant list, which is the host's
                  data and is refused by the server anyway. */}
              <div className="flex items-center gap-2">
                {event.can_edit !== false && (
                  <>
                    {/* Nothing to list: entries for this event live elsewhere,
                        and the server refuses the request anyway. */}
                    {!event.external_entry && (
                      <button
                        onClick={() => setEntrantsFor(event)}
                        className="text-ink-muted hover:text-ink"
                        title={t('entrantListOpen')}
                      >
                        <Users className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => openCopy(event)}
                      className="text-ink-muted hover:text-ink"
                      title={t('eventCopyTitle')}
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openEdit(event)}
                      className="text-blue-700 dark:text-blue-400 hover:opacity-80"
                      title={t('edit')}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deleteEvent(event)}
                      className="text-red-700 dark:text-red-400 hover:opacity-80"
                      title={t('delete')}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {entrantsFor && (
        <EntrantListView
          event={entrantsFor}
          token={token}
          onClose={() => { setEntrantsFor(null); loadEvents(); }}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-paper rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-line">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-ink">
                {editingId ? t('editEvent') : (copying ? t('eventCopyTitle') : t('addEvent'))}
              </h3>
              <button
                onClick={() => { setShowModal(false); setError(''); }}
                className="text-ink-muted hover:text-ink"
                title={t('close')}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Says up front what a copy did and did not bring, so the empty
                  date fields read as deliberate rather than as a bug. */}
              {copying && (
                <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-3 rounded text-sm">
                  {t('eventCopyDatesCleared')}
                </div>
              )}

              <h4 className="text-ink font-semibold border-b border-line pb-2">{t('eventBasicInfo')}</h4>

              {field('name', t('eventName'))}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('start_date', t('eventStartDate'), 'date')}
                {field('end_date', t('eventEndDateOptional'), 'date')}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('organizer', t('eventOrganizer'))}
                <div>
                  <label className="block text-ink-muted mb-2 font-semibold">{t('eventStatus')}</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  >
                    <option value="draft">{t('eventStatusDraft')}</option>
                    <option value="published">{t('eventStatusPublished')}</option>
                    <option value="cancelled">{t('eventStatusCancelled')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-ink-muted mb-2 font-semibold">{t('eventDescription')}</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                />
              </div>

              <h4 className="text-ink font-semibold border-b border-line pb-2 pt-2">{t('eventLocation')}</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('venue', t('eventVenue'))}
                {field('address', t('eventAddress'))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('city', t('eventCity'))}
                <div>
                  <label className="block text-ink-muted mb-2 font-semibold">{t('eventCountry')}</label>
                  <select
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  >
                    <CountryOptions withFlag={false} />
                  </select>
                </div>
              </div>

              {canMarkChampionship && (
                <div>
                  <label className="block text-ink-muted mb-2 font-semibold"
                         htmlFor="event-championship">
                    {t('eventChampionship')}
                  </label>
                  <select
                    id="event-championship"
                    value={form.championship || 'none'}
                    onChange={(e) => setForm({ ...form, championship: e.target.value })}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  >
                    {CHAMPIONSHIPS.map(level => (
                      <option key={level} value={level}>{championshipLabel(level)}</option>
                    ))}
                  </select>
                  <p className="text-sm text-ink-muted mt-1">{t('championshipHint')}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {field('contact_email', t('eventContactEmail'), 'email')}
                {field('contact_phone', t('eventContactPhone'))}
                {field('website', t('eventWebsite'))}
              </div>

              <h4 className="text-ink font-semibold border-b border-line pb-2 pt-2">{t('eventEntrySettings')}</h4>

              <div>
                <label className="block text-ink-muted mb-2 font-semibold">{t('eventSupportedTransponders')}</label>
                <div className="flex flex-wrap gap-3">
                  {TRANSPONDER_TYPES.map(type => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer bg-paper-alt px-3 py-2 rounded">
                      <input
                        type="checkbox"
                        checked={form.supported_transponders.includes(type.value)}
                        onChange={() => toggleTransponder(type.value)}
                        className="w-4 h-4"
                      />
                      <span className="text-ink text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('entry_opens_at', t('eventEntryOpens'), 'datetime-local')}
                {field('entry_closes_at', t('eventEntryCloses'), 'datetime-local')}
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.entry_list_public}
                    onChange={(e) => setForm({ ...form, entry_list_public: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-ink font-semibold">{t('eventEntryListPublic')}</span>
                </label>
                <p className="text-ink-muted text-sm mt-2">{t('eventEntryListPublicDescription')}</p>
              </div>

              {/* Entry taken somewhere else. The address only matters when the
                  box is ticked, so it is revealed by it rather than sitting
                  there asking to be filled in. */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.external_entry}
                    onChange={(e) => setForm({ ...form, external_entry: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-ink font-semibold">{t('eventExternalEntry')}</span>
                </label>
                <p className="text-ink-muted text-sm mt-2">{t('eventExternalEntryHint')}</p>

                {form.external_entry && (
                  <div className="mt-3">
                    <label className="block text-ink-muted mb-2 font-semibold"
                           htmlFor="event-external-entry-url">
                      {t('eventExternalEntryUrl')}{' '}
                      <span className="text-red-700 dark:text-red-400">*</span>
                    </label>
                    <input
                      id="event-external-entry-url"
                      type="url"
                      value={form.external_entry_url || ''}
                      onChange={(e) => setForm({ ...form, external_entry_url: e.target.value })}
                      placeholder="https://entry.example.com/race/123"
                      className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                    />
                  </div>
                )}
              </div>

              {/* Classes and extras need an event id, so they can only be
                  managed after the event exists. A copy has them waiting but
                  nowhere to put them yet, so it says what is coming instead. */}
              {editingEvent ? (
                <div className="pt-2 space-y-6">
                  <EventClassEditor
                    event={editingEvent}
                    series={series}
                    token={token}
                    onChange={loadEvents}
                  />
                  <EventQuestionEditor
                    event={editingEvent}
                    token={token}
                    onChange={loadEvents}
                  />
                </div>
              ) : copying && (
                <div className="bg-paper-alt rounded p-4 text-ink-muted text-sm">
                  {t('eventCopyCarries')
                    .replace('{classes}', copying.classes.length)
                    .replace('{extras}', copying.questions.length)}
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
                  {error}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveEvent}
                disabled={saving}
                className="flex-1 bg-accent hover:opacity-90 disabled:opacity-50 text-accent-ink py-2 rounded font-semibold"
              >
                {t('save')}
              </button>
              <button
                onClick={() => { setShowModal(false); setError(''); }}
                className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventManagement;
